/**
 * builderModel.js
 *
 * Manipulación pura del árbol. Sin React, sin fetch: todo aquí es una función
 * que recibe un árbol y devuelve otro nuevo. Eso hace que el undo/redo sea
 * trivial (guardar referencias) y que el editor no tenga estado escondido.
 */

export const BREAKPOINTS = ['desktop', 'tablet', 'mobile'];
export const CONTAINER_TYPES = ['section', 'row', 'column'];

export const ELEMENT_TYPES = [
  'heading', 'text', 'button', 'image', 'video', 'icon', 'divider', 'spacer',
  'list', 'form', 'booking', 'map', 'social', 'html', 'countdown',
  'testimonial', 'pricing', 'faq'
];

export const ALLOWED_CHILDREN = {
  root: ['section'],
  section: ['row'],
  row: ['column'],
  column: ELEMENT_TYPES
};

/** Elementos cuyo contenido se edita escribiendo directo sobre el lienzo. */
export const INLINE_EDITABLE = new Set(['heading', 'text', 'button', 'list', 'pricing', 'testimonial', 'faq']);

export function uid(prefix) {
  return (prefix || 'n') + '_' + Math.random().toString(36).slice(2, 9);
}

export function deepClone(v) {
  return typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v));
}

/* ------------------------------------------------------------------ *
 * Búsqueda
 * ------------------------------------------------------------------ */

export function findNode(tree, id) {
  for (const n of tree || []) {
    if (n.id === id) return n;
    if (n.children) {
      const found = findNode(n.children, id);
      if (found) return found;
    }
  }
  return null;
}

/** Camino desde la raíz hasta el nodo, para el breadcrumb del editor. */
export function findPath(tree, id, acc = []) {
  for (const n of tree || []) {
    const next = acc.concat(n);
    if (n.id === id) return next;
    if (n.children) {
      const found = findPath(n.children, id, next);
      if (found) return found;
    }
  }
  return null;
}

export function findParent(tree, id) {
  const path = findPath(tree, id);
  if (!path || path.length < 2) return null;
  return path[path.length - 2];
}

/* ------------------------------------------------------------------ *
 * Mutaciones (siempre devuelven un árbol nuevo)
 * ------------------------------------------------------------------ */

function mapTree(tree, fn) {
  return (tree || []).map((n) => {
    const next = fn(n);
    if (next && next.children) {
      return Object.assign({}, next, { children: mapTree(next.children, fn) });
    }
    return next;
  }).filter(Boolean);
}

export function updateNode(tree, id, patch) {
  return mapTree(tree, (n) => {
    if (n.id !== id) return n;
    return typeof patch === 'function' ? patch(n) : Object.assign({}, n, patch);
  });
}

export function updateProps(tree, id, props) {
  return updateNode(tree, id, (n) => Object.assign({}, n, { props: Object.assign({}, n.props, props) }));
}

/**
 * Escribe una propiedad de estilo en el breakpoint activo.
 * Valor `null`/`''` borra la sobreescritura, que es como se "vuelve a heredar".
 */
export function updateStyle(tree, id, breakpoint, key, value) {
  return updateNode(tree, id, (n) => {
    const style = Object.assign({}, n.style);
    const bp = Object.assign({}, style[breakpoint]);
    if (value === null || value === undefined || value === '') delete bp[key];
    else bp[key] = value;
    if (Object.keys(bp).length) style[breakpoint] = bp;
    else delete style[breakpoint];
    return Object.assign({}, n, { style });
  });
}

export function updateAdvanced(tree, id, patch) {
  return updateNode(tree, id, (n) =>
    Object.assign({}, n, { advanced: Object.assign({}, n.advanced, patch) }));
}

export function removeNode(tree, id) {
  return mapTree(tree, (n) => (n.id === id ? null : n));
}

/** Duplica un nodo justo debajo del original, con ids nuevos en todo el subárbol. */
export function duplicateNode(tree, id) {
  const regen = (n) => {
    const copy = Object.assign({}, deepClone(n), { id: uid(n.type.slice(0, 3)) });
    if (copy.children) copy.children = copy.children.map(regen);
    return copy;
  };
  const walk = (nodes) => {
    const out = [];
    for (const n of nodes || []) {
      const next = n.children ? Object.assign({}, n, { children: walk(n.children) }) : n;
      out.push(next);
      if (n.id === id) out.push(regen(n));
    }
    return out;
  };
  return walk(tree);
}

/**
 * Inserta `node` respecto a `targetId`.
 * @param {'before'|'after'|'inside'} position
 */
export function insertNode(tree, targetId, position, node) {
  if (!targetId) return (tree || []).concat([node]);

  const walk = (nodes) => {
    const out = [];
    for (const n of nodes || []) {
      if (n.id === targetId) {
        if (position === 'before') out.push(node, n);
        else if (position === 'after') out.push(n, node);
        else {
          out.push(Object.assign({}, n, { children: (n.children || []).concat([node]) }));
        }
        continue;
      }
      out.push(n.children ? Object.assign({}, n, { children: walk(n.children) }) : n);
    }
    return out;
  };
  return walk(tree);
}

export function moveNode(tree, id, targetId, position) {
  if (id === targetId) return tree;
  const node = findNode(tree, id);
  if (!node) return tree;
  // Mover un contenedor dentro de sí mismo destruiría el árbol.
  if (findNode([node], targetId)) return tree;
  return insertNode(removeNode(tree, id), targetId, position, deepClone(node));
}

/** Sube o baja un nodo entre sus hermanos. */
export function reorderSibling(tree, id, delta) {
  const walk = (nodes) => {
    const idx = nodes.findIndex((n) => n.id === id);
    if (idx >= 0) {
      const next = idx + delta;
      if (next < 0 || next >= nodes.length) return nodes;
      const copy = nodes.slice();
      const [item] = copy.splice(idx, 1);
      copy.splice(next, 0, item);
      return copy;
    }
    return nodes.map((n) => (n.children ? Object.assign({}, n, { children: walk(n.children) }) : n));
  };
  return walk(tree || []);
}

export function canDrop(childType, parentType) {
  return (ALLOWED_CHILDREN[parentType] || []).includes(childType);
}

/* ------------------------------------------------------------------ *
 * Estilos: cascada y CSS
 * ------------------------------------------------------------------ */

/** Estilo efectivo en un breakpoint, con la cascada desktop → tablet → móvil. */
export function effectiveStyle(node, breakpoint) {
  const s = node.style || {};
  if (breakpoint === 'desktop') return Object.assign({}, s.desktop);
  if (breakpoint === 'tablet') return Object.assign({}, s.desktop, s.tablet);
  return Object.assign({}, s.desktop, s.tablet, s.mobile);
}

/** `true` si la clave está sobreescrita EN ESTE breakpoint (no heredada). */
export function isOverridden(node, breakpoint, key) {
  return !!(node.style && node.style[breakpoint] && node.style[breakpoint][key] !== undefined);
}

const UNITLESS = new Set(['fontWeight', 'lineHeight', 'opacity', 'zIndex', 'flexGrow', 'flexShrink']);

export function toReactStyle(styleObj) {
  const out = {};
  for (const [k, v] of Object.entries(styleObj || {})) {
    out[k] = typeof v === 'number' && !UNITLESS.has(k) ? v + 'px' : v;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Tema
 * ------------------------------------------------------------------ */

export const DEFAULT_THEME = {
  colors: {
    primary: '#0F62FE', secondary: '#0B3B8C', accent: '#F5A524',
    text: '#1A1A1A', muted: '#6B7280', bg: '#FFFFFF', surface: '#F6F7F9', border: '#E5E7EB'
  },
  fonts: { heading: { family: 'Poppins', weight: 700 }, body: { family: 'Inter', weight: 400 } },
  radius: 12,
  maxWidth: 1140
};

export const FONT_OPTIONS = [
  'Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Nunito',
  'Raleway', 'Playfair Display', 'Merriweather', 'Oswald', 'Source Sans Pro',
  'Work Sans', 'DM Sans', 'Bebas Neue', 'Georgia', 'Arial', 'Times New Roman'
];

/** Paletas listas. Cambiar de paleta re-tematiza el sitio entero de una vez. */
export const COLOR_PRESETS = [
  { name: 'Azul confianza', colors: { primary: '#0F62FE', secondary: '#0B3B8C', accent: '#F5A524', surface: '#F5F8FF', border: '#E4E7EC' } },
  { name: 'Rojo apetito', colors: { primary: '#C1121F', secondary: '#780000', accent: '#F5A524', surface: '#FFF8F0', border: '#EADDD0' } },
  { name: 'Verde natural', colors: { primary: '#15803D', secondary: '#166534', accent: '#F59E0B', surface: '#F0FDF4', border: '#BBF7D0' } },
  { name: 'Violeta premium', colors: { primary: '#7C3AED', secondary: '#4C1D95', accent: '#F59E0B', surface: '#F8F5FF', border: '#E9D5FF' } },
  { name: 'Turquesa fresco', colors: { primary: '#0EA5E9', secondary: '#0369A1', accent: '#22C55E', surface: '#F0F9FF', border: '#BAE6FD' } },
  { name: 'Negro elegante', colors: { primary: '#111827', secondary: '#374151', accent: '#D4AF37', surface: '#F9FAFB', border: '#E5E7EB' } },
  { name: 'Rosa boutique', colors: { primary: '#DB2777', secondary: '#9D174D', accent: '#F472B6', surface: '#FDF2F8', border: '#FBCFE8' } },
  { name: 'Naranja energía', colors: { primary: '#EA580C', secondary: '#9A3412', accent: '#FACC15', surface: '#FFF7ED', border: '#FED7AA' } }
];

export const FONT_PAIRS = [
  { name: 'Moderno', heading: 'Poppins', body: 'Inter' },
  { name: 'Editorial', heading: 'Playfair Display', body: 'Inter' },
  { name: 'Corporativo', heading: 'Montserrat', body: 'Open Sans' },
  { name: 'Cercano', heading: 'Nunito', body: 'Nunito' },
  { name: 'Impacto', heading: 'Bebas Neue', body: 'Work Sans' },
  { name: 'Clásico', heading: 'Merriweather', body: 'Georgia' }
];

export function themeToCssVars(theme) {
  const t = theme || DEFAULT_THEME;
  const vars = {};
  for (const [k, v] of Object.entries(t.colors || {})) vars['--c-' + k] = v;
  vars['--f-heading'] = t.fonts?.heading?.family || 'Poppins';
  vars['--f-body'] = t.fonts?.body?.family || 'Inter';
  vars['--radius'] = (t.radius ?? 12) + 'px';
  vars['--maxw'] = (t.maxWidth ?? 1140) + 'px';
  vars['--gap'] = '0px';
  return vars;
}

/** Carga las fuentes del tema. Idempotente: no duplica <link> al cambiar de tema. */
export function ensureFontsLoaded(theme) {
  if (typeof document === 'undefined') return;
  const fams = [theme?.fonts?.heading?.family, theme?.fonts?.body?.family]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i);
  if (!fams.length) return;
  const id = 'builder-fonts-' + fams.join('-').replace(/\W/g, '');
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?' +
    fams.map((f) => 'family=' + f.replace(/ /g, '+') + ':wght@300;400;500;600;700;800').join('&') +
    '&display=swap';
  document.head.appendChild(link);
}

/* ------------------------------------------------------------------ *
 * Historial (undo / redo)
 * ------------------------------------------------------------------ */

const HISTORY_LIMIT = 60;
const COALESCE_MS = 400;

export function createHistory(initial) {
  return { past: [], present: initial, future: [], lastPush: 0 };
}

/**
 * `coalesce` junta cambios seguidos del mismo tipo dentro de 400 ms, para que
 * escribir una frase no genere una entrada de historial por letra.
 */
export function pushHistory(h, next, coalesce) {
  const now = Date.now();
  if (coalesce && now - h.lastPush < COALESCE_MS && h.past.length) {
    return Object.assign({}, h, { present: next, future: [], lastPush: now });
  }
  const past = h.past.concat([h.present]).slice(-HISTORY_LIMIT);
  return { past, present: next, future: [], lastPush: now };
}

export function undo(h) {
  if (!h.past.length) return h;
  const previous = h.past[h.past.length - 1];
  return {
    past: h.past.slice(0, -1),
    present: previous,
    future: [h.present].concat(h.future),
    lastPush: 0
  };
}

export function redo(h) {
  if (!h.future.length) return h;
  return {
    past: h.past.concat([h.present]),
    present: h.future[0],
    future: h.future.slice(1),
    lastPush: 0
  };
}

/* ------------------------------------------------------------------ *
 * Utilidades varias
 * ------------------------------------------------------------------ */

export function countNodes(tree) {
  let n = 0;
  const walk = (nodes) => {
    for (const x of nodes || []) { n += 1; if (x.children) walk(x.children); }
  };
  walk(tree);
  return n;
}

export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try { resolve(JSON.parse(String(reader.result))); }
      catch (e) { reject(new Error('El archivo no es un JSON válido')); }
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsText(file);
  });
}
